import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { SettingsHttpService } from '../../../core/settings';
import { map, catchError, take } from 'rxjs/operators';

export interface ResolvedMachine {
	machine_id: string;
	machine_name?: string;
	serial_no?: string;
	[key: string]: unknown;
}

/**
 * Resolves machine by machineId from the route and attaches it to route.data.machine.
 * When navigating from the machine list, the list can pass the machine in state to avoid a redundant API call.
 */
@Injectable({ providedIn: 'root' })
export class MachineResolver implements Resolve<ResolvedMachine | null> {
	constructor(private settingsHttp: SettingsHttpService, private router: Router) {}

	resolve(route: ActivatedRouteSnapshot): Observable<ResolvedMachine | null> {
		const machineId = route.paramMap.get('machineId');
		if (!machineId) {
			return of(null);
		}
		const nav = this.router.getCurrentNavigation();
		const state = nav && nav.extras && nav.extras.state;
		const stateMachine = state && state['machine'];
		const rec = stateMachine as Record<string, unknown> | null | undefined;
		const stateId = rec != null && rec.machine_id != null ? rec.machine_id : (rec != null && rec.m_id != null ? rec.m_id : undefined);
		if (stateMachine && stateId === machineId) {
			const normalized = this.normalizeMachine(stateMachine as Record<string, unknown>, machineId);
			return of(normalized);
		}
		return this.settingsHttp.getMachineDet(machineId).pipe(
			take(1),
			map((data: Record<string, unknown>) => {
				if (!data) return null;
				return this.normalizeMachine(data, machineId);
			}),
			catchError(() => of(null))
		);
	}

	private normalizeMachine(data: Record<string, unknown>, machineId: string): ResolvedMachine {
		const machineIdVal = data.machine_id != null ? data.machine_id : (data.m_id != null ? data.m_id : machineId);
		return {
			...data,
			machine_id: machineIdVal as string,
			serial_no: data.serial_no,
			machine_name: data.machine_name
		} as ResolvedMachine;
	}
}
